
/**
 * Pairs a technical varName with its user-friendly display name.
 * Used wherever validation output needs to be shown to end users.
 */
export class ControlValidationName {

  name?: string | null;
  varName?: string | null;
  src?: string | null;

  constructor(varName?: string | null, name?: string | null, src: string | null = null) {
    this.varName = varName;
    this.name = name;
    this.src = src;
  }

  /** Prefers the user-friendly name; falls back to varName; finally to empty string. */
  get displayIdentifier(): string {
    const base = this.name ?? this.varName ?? '';
    return this.src ? `Tower '${this.src}': ${base}` : base;
  }

  toString(): string {
    return this.displayIdentifier;
  }

  toJSON(): object {
    return { varName: this.varName, name: this.name, src: this.src };
  }
}

/**
 * Represents validation details for a single form control.
 * Now carries a display name in addition to the technical varName.
 */
export class ControlValidationError {
  constructor(
    public readonly control: ControlValidationName,
    public readonly errors: Record<string, unknown>
  ) { }

  /** Technical varName (kept for backward compatibility with callers). */
  get controlName(): string {
    return this.control.varName ?? '';
  }

  /** User-friendly label — display name preferred, varName as fallback. */
  get displayName(): string {
    return this.control.displayIdentifier;
  }

  /** Debug form: `displayName [varName] (error1, error2)` */
  toString(): string {
    const errorKeys = Object.keys(this.errors).join(', ');
    const label = this.control.name
      ? `${this.control.name} [${this.control.varName}]`
      : this.control.varName ?? '';
    return `${label} (${errorKeys})`;
  }

  /** User-facing form: just the display name, no technical details. */
  toUserString(): string {
    return this.displayName;
  }

  toJSON(): object {
    return {
      control: this.control.toJSON(),
      errors: this.errors
    };
  }
}

export class FormValidationDetails {
  constructor(
    public readonly formId: string,
    public readonly formDisplayName: string | null = null,
    public readonly invalidControls: ControlValidationError[] = [],
    public readonly invalidGrids: ControlValidationName[] = []  // was string[]
  ) { }

  get isInvalid(): boolean {
    return this.invalidControls.length > 0 || this.invalidGrids.length > 0;
  }

  get controlCount(): number { return this.invalidControls.length; }
  get gridCount(): number { return this.invalidGrids.length; }
  get totalErrorCount(): number { return this.controlCount + this.gridCount; }
  get displayIdentifier(): string { return this.formDisplayName ?? this.formId; }

  hasInvalidControl(varName: string): boolean {
    return this.invalidControls.some(c => c.control.varName === varName);
  }

  hasInvalidGrid(varName: string): boolean {
    return this.invalidGrids.some(g => g.varName === varName);
  }

  /** Debug string — shows technical details. */
  toString(): string {
    if (!this.isInvalid) {
      return `Form "${this.displayIdentifier}": Valid`;
    }

    const parts: string[] = [`Form "${this.displayIdentifier}":`];

    if (this.invalidControls.length > 0) {
      const controlNames = this.invalidControls.map(c => c.toString()).join(', ');
      parts.push(`Invalid controls (${this.controlCount}): ${controlNames}`);
    }

    if (this.invalidGrids.length > 0) {
      const gridLabels = this.invalidGrids
        .map(g => g.name ? `${g.name} [${g.varName}]` : g.varName ?? '')
        .join(', ');
      parts.push(`Invalid grids (${this.gridCount}): ${gridLabels}`);
    }

    return parts.join(' ');
  }

  /** User-facing list of invalid control display names (excludes maxlength-only and slash-only errors). */
  getInvalidControlDisplayNames(): string[] {
    return this.invalidControls
      .filter(c => (!c.errors['maxlength'] && !c.errors['slashNotAllowed']) || c.errors['required'] || c.errors['whitespace'])
      .map(c => c.displayName)
      .filter(n => n.length > 0);
  }

  /** Returns maxlength errors with display name and the allowed length. */
  getMaxLengthErrors(): { displayName: string; requiredLength: number }[] {
    return this.invalidControls
      .filter(c => c.errors['maxlength'])
      .map(c => ({
        displayName: c.displayName,
        requiredLength: (c.errors['maxlength'] as { requiredLength: number }).requiredLength
      }));
  }

  /** Returns slash-character errors with display name. */
  getSlashErrors(): { displayName: string }[] {
    return this.invalidControls
      .filter(c => c.errors['slashNotAllowed'])
      .map(c => ({ displayName: c.displayName }));
  }

  /** User-facing list of invalid grid display names. */
  getInvalidGridDisplayNames(): string[] {
    return this.invalidGrids.map(g => g.displayIdentifier).filter(n => n.length > 0);
  }

  toJSON(): object {
    return {
      formId: this.formId,
      formDisplayName: this.formDisplayName,
      isInvalid: this.isInvalid,
      invalidControls: this.invalidControls.map(c => c.toJSON()),
      invalidGrids: this.invalidGrids.map(g => g.toJSON()),
      controlCount: this.controlCount,
      gridCount: this.gridCount
    };
  }
}

export class DealValidationResult {
  constructor(
    public readonly mainForm: FormValidationDetails | null = null,
    public readonly componentGrids: ControlValidationName[] = [],  // was string[]
    public readonly cachedForms: FormValidationDetails[] = []
  ) { }

  get isValid(): boolean {
    return !this.mainForm?.isInvalid &&
      this.componentGrids.length === 0 &&
      !this.cachedForms.some(f => f.isInvalid);
  }

  get isInvalid(): boolean { return !this.isValid; }

  get totalErrorCount(): number {
    let count = this.mainForm?.totalErrorCount ?? 0;
    count += this.componentGrids.length;
    count += this.cachedForms.reduce((sum, f) => sum + f.totalErrorCount, 0);
    return count;
  }

  get invalidCachedFormIds(): string[] {
    return this.cachedForms.filter(f => f.isInvalid).map(f => f.formId);
  }

  get invalidCachedFormNames(): string[] {
    return this.cachedForms.filter(f => f.isInvalid).map(f => f.displayIdentifier);
  }

  hasCachedFormError(formId: string): boolean {
    return this.cachedForms.some(f => f.formId === formId && f.isInvalid);
  }

  getCachedFormDetails(formId: string): FormValidationDetails | undefined {
    return this.cachedForms.find(f => f.formId === formId);
  }

  /** User-friendly summary — uses display names throughout. */
  toUserMessage(): string {
    if (this.isValid) {
      return 'All validations passed';
    }

    const messages: string[] = [];

    // Main form — list the actual invalid field names
    if (this.mainForm?.isInvalid) {
      if (this.mainForm.controlCount > 0) {
        const names = this.mainForm.getInvalidControlDisplayNames();
        const maxLengthErrors = this.mainForm.getMaxLengthErrors();
        const slashErrors = this.mainForm.getSlashErrors();

        if (names.length > 0)
          messages.push(`Required fields missing: ${names.join(', ')}`);
        else if (maxLengthErrors.length === 0 && slashErrors.length === 0)
          messages.push(`Required fields missing in current form`);

        for (const err of maxLengthErrors)
          messages.push(`${err.displayName}. Maximum length is ${err.requiredLength} characters`);

        for (const err of slashErrors)
          messages.push(`${err.displayName}: Slash characters ( / and \\ ) are not allowed`);
      }
      if (this.mainForm.gridCount > 0) {
        const names = this.mainForm.getInvalidGridDisplayNames();
        messages.push(
          names.length > 0
            ? `Invalid grid data: ${names.join(', ')}`
            : `Invalid grid data in current form`
        );
      }
    }

    // Component grid errors — use display names
    if (this.componentGrids.length > 0) {
      const names = this.componentGrids.map(g => g.displayIdentifier).filter(n => n.length > 0);
      messages.push(`Invalid grids: ${names.join(', ')}`);
    }

    // Cached form errors
    const invalidCached = this.cachedForms.filter(f => f.isInvalid);
    if (invalidCached.length > 0) {
      for (const form of invalidCached) {
        const formName = form.displayIdentifier;
        if (form.controlCount > 0 && form.gridCount > 0) {
          messages.push(`"${formName}" has invalid fields and grids`);
        } else if (form.controlCount > 0) {
          messages.push(`"${formName}" has invalid fields`);
        } else if (form.gridCount > 0) {
          messages.push(`"${formName}" has invalid grids`);
        }
      }
    }

    return messages.length > 0
      ? messages.join('. ') + '.'
      : 'Some required fields have not been specified, please fill before saving';
  }

  toString(): string {
    if (this.isValid) return 'DealValidationResult: Valid';

    const parts: string[] = ['DealValidationResult: INVALID'];

    if (this.mainForm?.isInvalid) {
      parts.push(`  [Main Form] ${this.mainForm.toString()}`);
    }

    if (this.componentGrids.length > 0) {
      const labels = this.componentGrids
        .map(g => g.name ? `${g.name} [${g.varName}]` : g.varName ?? '')
        .join(', ');
      parts.push(`  [Component Grids] Invalid (${this.componentGrids.length}): ${labels}`);
    }

    const invalidCached = this.cachedForms.filter(f => f.isInvalid);
    if (invalidCached.length > 0) {
      parts.push(`  [Cached Forms] ${invalidCached.length} invalid:`);
      for (const form of invalidCached) {
        parts.push(`    - ${form.toString()}`);
      }
    }

    return parts.join('\n');
  }

  toJSON(): object {
    return {
      isValid: this.isValid,
      totalErrorCount: this.totalErrorCount,
      mainForm: this.mainForm?.toJSON() ?? null,
      componentGrids: this.componentGrids.map(g => g.toJSON()),
      cachedForms: this.cachedForms.map(f => f.toJSON()),
      invalidCachedFormIds: this.invalidCachedFormIds,
      invalidCachedFormNames: this.invalidCachedFormNames
    };
  }
}
